/**
 * Custom message components for the assistant-ui Thread.
 *
 * - MarkdownText: Rich markdown rendering via assistant-ui
 * - ToolCallBadge: Inline success/failure indicator for tool executions
 * - ConfirmationCard: Approve/reject card for destructive actions
 * - EmptyState: Welcome message with example prompts
 */

import { MarkdownTextPrimitive } from "@assistant-ui/react-markdown"
import remarkGfm from "remark-gfm"

// ── MarkdownText ────────────────────────────────────────────

export function MarkdownText() {
  return <MarkdownTextPrimitive remarkPlugins={[remarkGfm]} />
}

// ── ToolCallBadge ───────────────────────────────────────────

interface ToolCallBadgeProps {
  toolName: string
  success: boolean
  error?: string
  isRunning?: boolean
}

export function ToolCallBadge({ toolName, success, error, isRunning }: ToolCallBadgeProps) {
  return (
    <div className="mt-1 pt-1 border-t border-border/50 flex items-center gap-1">
      <span className="text-xs font-mono text-muted-foreground">{toolName}</span>
      {isRunning ? (
        <span className="text-xs text-muted-foreground animate-pulse">...</span>
      ) : success ? (
        <svg className="size-3 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      ) : (
        <>
          <svg className="size-3 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
          {error && (
            <span className="text-xs text-red-500 truncate max-w-[200px]">
              {error.length > 80 ? error.slice(0, 80) + "..." : error}
            </span>
          )}
        </>
      )}
    </div>
  )
}

// ── ConfirmationCard ────────────────────────────────────────
// Structural source of truth: blocks-v2/agent/confirmation-card/ (5 Cogo JSON files)
// Rendered as direct JSX because BlockV2 requires RenderContext.Provider which is
// not available inside AgentPanel's assistant-ui Thread context.
// When BlockV2 gains standalone rendering support, this can be reverted to use it.

/**
 * Extract a human-readable verb from a tool name for button text.
 * delete_record → "delete", checkout → "proceed to checkout", pay_order → "pay"
 */
function extractVerb(toolName: string): string {
  const name = toolName.replace(/_/g, " ")
  if (name.startsWith("delete")) return "delete"
  if (name === "checkout") return "proceed to checkout"
  if (name.startsWith("pay")) return "pay"
  if (name.startsWith("clear")) return "clear cart"
  if (name.startsWith("remove")) return "remove"
  if (name.startsWith("set state")) return "set state"
  return name
}

interface ConfirmationCardProps {
  toolName: string
  toolDescription: string
  args: Record<string, unknown>
  isDestructive: boolean
  onApprove: () => void
  onReject: () => void
  resolved?: boolean
}

/**
 * ConfirmationCard — Approve/reject card for destructive agent actions.
 *
 * Structure mirrors blocks-v2/agent/confirmation-card/ui.cogo.json.
 * Direct JSX rendering (BlockV2 requires RenderContext.Provider not available here).
 */
export function ConfirmationCard({
  toolName,
  toolDescription,
  args,
  isDestructive,
  onApprove,
  onReject,
  resolved,
}: ConfirmationCardProps) {
  const containerClass = isDestructive
    ? "rounded-xl p-3 border bg-red-50 border-red-300"
    : "rounded-xl p-3 border bg-amber-50 border-amber-300"
  const textClass = isDestructive ? "text-red-900" : "text-amber-900"
  const resolvedClass = resolved ? "opacity-50 pointer-events-none" : ""
  const messageText = isDestructive
    ? `This action cannot be undone. Are you sure?`
    : `I'll ${toolDescription}. Should I go ahead?`
  const approveText = isDestructive ? `Yes, ${extractVerb(toolName)}` : "Approve"
  const dismissText = isDestructive ? "No, keep it" : "Dismiss"

  return (
    <div data-block="confirmation-card" data-destructive={isDestructive} className={containerClass}>
      <p className={`text-sm mb-2 ${textClass}`}>{messageText}</p>
      {Object.keys(args).length > 0 && (
        <div className="mb-2 text-xs font-mono text-muted-foreground space-y-0.5">
          {Object.entries(args).map(([key, val]) => (
            <div key={key}>{key}={typeof val === "string" ? val : JSON.stringify(val)}</div>
          ))}
        </div>
      )}
      <div className={`flex gap-2 mt-2 ${resolvedClass}`}>
        <button
          onClick={onReject}
          disabled={!!resolved}
          className="flex-1 px-3 py-1.5 text-sm rounded-md border border-input bg-background hover:bg-accent"
        >
          {dismissText}
        </button>
        <button
          onClick={onApprove}
          disabled={!!resolved}
          className={`flex-1 px-3 py-1.5 text-sm rounded-md text-white ${
            isDestructive ? "bg-red-600 hover:bg-red-700" : "bg-primary hover:bg-primary/90"
          }`}
        >
          {approveText}
        </button>
      </div>
    </div>
  )
}

// ── EmptyState ──────────────────────────────────────────────

interface EmptyStateProps {
  greeting: string
}

export function EmptyState({ greeting }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-6">
      <p className="text-sm font-semibold text-foreground mb-2">{greeting}</p>
      <p className="text-xs text-muted-foreground">
        Try: "Add Premium Dog Food to cart" or "Show my orders"
      </p>
    </div>
  )
}
